import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import produce from 'immer';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  TransitionSource,
  TaskSchemaNodeSection,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  TaskSchemaNode,
  FlowchartNode,
  isInteractiveTaskNode,
  getInteractiveTaskNodeDatas,
  isInteractiveRouterNode,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {
  FlowchartNodeChange,
  FlowchartNodeChangeType,
  FlowchartTransitionSourceChange,
  FlowchartTransitionSourceChangeType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorUtils';
import {
  getDefaultTransitionSource,
  getTransitionSourceByNodeIdPairKey,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/TransitionSourceUtils';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {ORANGE_DARK} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';

import {findAllUpstreamMappableNodeIds, getRouterNodeIdsBetweenNodes} from '../TraverseFlowchart';
import {NodeDetailsSidepanelContext} from './NodeDetailsSidepanelContext';

function getOptionTextForTransitionSource(transitionSource: TransitionSource): string {
  switch (transitionSource) {
    case TransitionSource.OUTPUTS:
      return 'Outputs';
    case TransitionSource.INPUTS:
      return 'Tasks';
    default:
      throw new Error(`Unexpected transition source: ${transitionSource}`);
  }
}

export interface FlowchartSettingsTabContentProps {
  locked?: boolean;
}

interface SettingsContentProps {
  selectedNodeName: string;
  connectedNodes: Array<TaskSchemaNode>;
  transitionSources: Record<string, TransitionSource>;
  handleTransitionSourceSelect: (upstreamMappableNodeIdToUpdate: string, value: TransitionSource) => void;
  locked?: boolean;
}

function EmptySettingsContent(props: {selectedNodeName: string}): JSX.Element {
  const {selectedNodeName} = props;
  return (
    <div className={css(styles.emptyStateContainer)}>
      <div className={css(styles.flowchartSettingDescription)}>
        There are no mappings to configure for
        <span className={css(styles.flowchartSettingDescriptionBold)}>{selectedNodeName}</span>.
      </div>
    </div>
  );
}

function SettingsContent(props: SettingsContentProps): JSX.Element {
  const {selectedNodeName, connectedNodes, transitionSources, handleTransitionSourceSelect, locked} = props;

  return (
    <>
      <div className={css(styles.flowchartSettingDescription)}>
        <span>Define the task creation & field population behavior for each transition into&nbsp;</span>
        <Chip text={selectedNodeName} size={ChipSize.MINI} />
      </div>
      <Table
        numRows={connectedNodes.length}
        calculateInView={false}
        fitContent={false}
        frozenHeader={false}
        getRowKey={(rowIndex) => connectedNodes[rowIndex].id}
        includeNegativeMargins={false}
        isCompact={false}
        overflowVisible={false}
        tableLayout="fixed"
      >
        <Column
          header={<Cell className={css(styles.fromColumn)}>From</Cell>}
          renderCell={(rowIdx: number) => {
            const node = connectedNodes[rowIdx];
            return (
              <Cell className={css(styles.fromColumn, styles.wrapCellText)}>
                <Chip text={node.data.nodeName} size={ChipSize.MINI} />
              </Cell>
            );
          }}
        />
        <Column
          header="Behavior"
          renderCell={(rowIdx: number) => {
            const node = connectedNodes[rowIdx];
            const isRootNode =
              node.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS;
            const isOutputNode =
              node.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS;

            const hasOutputSection = node.data.sections.some(
              (section) => section.sectionName === TaskSchemaNodeSection.OUTPUTS
            );
            const availableTransitionSources = hasOutputSection
              ? Object.values(TransitionSource)
              : [TransitionSource.INPUTS];

            return (
              <Cell className={css(styles.tableCell, styles.wrapCellText)}>
                {isRootNode ? (
                  <div>
                    <span>The flowchart's tasks will be used to create tasks &amp; populate fields.</span>
                  </div>
                ) : (
                  <>
                    <span>Use &nbsp;</span>
                    <div className={css(styles.fieldSourceDropdownWrapper)}>
                      <SelectDropdown
                        dataTestAttrs={{
                          component: 'TransitionSourceDropdown',
                          element: 'root',
                          key: node.data.nodeName,
                        }}
                        menu={
                          <Menu
                            height={Menu.HEIGHTS.MEDIUM}
                            width={Menu.WIDTHS.LARGE}
                            value={transitionSources[node.id]}
                          >
                            {availableTransitionSources.map((transitionSource) => (
                              <Option
                                key={transitionSource}
                                value={transitionSource}
                                onSelect={(value: TransitionSource) => {
                                  handleTransitionSourceSelect(node.id, value);
                                }}
                                isDisabled={locked}
                              >
                                {getOptionTextForTransitionSource(transitionSource)}
                              </Option>
                            ))}
                          </Menu>
                        }
                        displayName={getOptionTextForTransitionSource(transitionSources[node.id])}
                      />
                    </div>
                    {isOutputNode ? (
                      <span>&nbsp;to create tasks &amp; populate fields</span>
                    ) : (
                      <span>&nbsp;to populate output fields on this flowchart</span>
                    )}
                    {transitionSources[node.id] === TransitionSource.INPUTS && (
                      <div className={css(styles.warningMessage)}>
                        <BenchlingIcon icon="warning" className={css(styles.warningIcon)} />
                        <span className={css(styles.secondaryText)}>
                          Limits field mappings to input fields from the previous step
                        </span>
                      </div>
                    )}
                  </>
                )}
              </Cell>
            );
          }}
        />
      </Table>
    </>
  );
}

function areAllNodesInteractiveTaskNodes(nodes: Array<FlowchartNode>): nodes is Array<TaskSchemaNode> {
  return nodes.every((node) => isInteractiveTaskNode(node));
}

export default function FlowchartSettingsPane(props: FlowchartSettingsTabContentProps): JSX.Element {
  const {selectedNode, interactiveFlowchart, transitionSourceByNodeIdPair, applyFlowchartChanges} =
    NodeDetailsSidepanelContext.useContext();

  const {upstreamMappableNodeIds, transitionSources} = useMemo(() => {
    const upstreamMappableNodeIds = findAllUpstreamMappableNodeIds(interactiveFlowchart, selectedNode.id);
    const transitionSources: Record<string, TransitionSource> = {};
    for (const sourceNodeId of upstreamMappableNodeIds) {
      const nodeIdPair = getTransitionSourceByNodeIdPairKey(sourceNodeId, selectedNode.id);
      transitionSources[sourceNodeId] =
        transitionSourceByNodeIdPair[nodeIdPair] ??
        getDefaultTransitionSource(sourceNodeId, getInteractiveTaskNodeDatas(interactiveFlowchart.nodes));
    }
    return {upstreamMappableNodeIds, transitionSources};
  }, [interactiveFlowchart, selectedNode.id, transitionSourceByNodeIdPair]);

  const {nodes} = interactiveFlowchart;
  const connectedNodes = Object.values(nodes).filter((node) => upstreamMappableNodeIds.includes(node.id));
  assert(areAllNodesInteractiveTaskNodes(connectedNodes), 'Expected all connected nodes to be task nodes');

  const handleTransitionSourceSelect = (
    upstreamMappableNodeIdToUpdate: string,
    value: TransitionSource
  ): void => {
    const currentTransitionSource = transitionSources[upstreamMappableNodeIdToUpdate];
    const nodeIdPair = getTransitionSourceByNodeIdPairKey(upstreamMappableNodeIdToUpdate, selectedNode.id);

    applyFlowchartChanges((flowchartDraftState) => {
      const nodeChanges: Array<FlowchartNodeChange> = [];
      const transitionSourceChanges: Array<FlowchartTransitionSourceChange> = [];

      if (currentTransitionSource !== value) {
        const routerNodeIds = getRouterNodeIdsBetweenNodes(
          interactiveFlowchart,
          upstreamMappableNodeIdToUpdate,
          selectedNode.id
        );
        for (const routerNodeId of routerNodeIds) {
          const routerNode = nodes.find((node) => node.id === routerNodeId)!;
          assert(isInteractiveRouterNode(routerNode), 'Expected router node');
          // When the transition source is changed, we need to clear the expressions on all router nodes between
          // the two nodes because the router configurations depend on the transition source.
          // We do not need to clear the default function spec because it does not have an expression associated
          // with it.
          const newData = produce(routerNode.data, (draft) => {
            draft.routerConfig.functionSpecs = draft.routerConfig.functionSpecs.map((spec) => ({
              ...spec,
              expression: null,
              isConfigured: false,
            }));
          });
          nodeChanges.push({
            id: routerNodeId,
            type: FlowchartNodeChangeType.REPLACE,
            data: newData,
          });
        }
      }

      transitionSourceChanges.push({
        type: FlowchartTransitionSourceChangeType.REPLACE,
        id: nodeIdPair,
        transitionSource: value,
      });

      return {
        nodeChanges,
        transitionSourceChanges,
      };
    });
  };

  return (
    <div className={css(styles.tabContentContainer)} key={selectedNode.id}>
      <div className={css(styles.settingsGroup)}>
        <div className={css(styles.flowchartSettingTitle)}>Transition behavior</div>
        {connectedNodes.length === 0 ? (
          <EmptySettingsContent selectedNodeName={selectedNode.data.nodeName} />
        ) : (
          <SettingsContent
            selectedNodeName={selectedNode.data.nodeName}
            connectedNodes={connectedNodes}
            transitionSources={transitionSources}
            handleTransitionSourceSelect={handleTransitionSourceSelect}
            locked={props.locked}
          />
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  tabContentContainer: {
    padding: '16px',
  },
  settingsGroup: {},
  flowchartSettingTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: Color.GRAY_900,
    marginBottom: '4px',
  },
  flowchartSettingDescription: {
    fontSize: '14px',
    fontWeight: 400,
    color: Color.GRAY_900,
    flexWrap: 'nowrap',
    marginBottom: '10px',
  },
  warningMessage: {
    marginTop: '4px',
  },
  flowchartSettingDescriptionBold: {
    fontWeight: 600,
    paddingLeft: '4px',
  },
  fromColumn: {
    width: '33%',
  },
  wrapCellText: {
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
  },
  tableCell: {
    wordBreak: 'break-word',
  },
  primaryText: {
    color: Color.GRAY_900,
  },
  secondaryText: {
    color: Color.GRAY_600,
  },
  warningIcon: {
    paddingRight: 4,
    color: ORANGE_DARK,
  },
  emptyStateContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  fieldSourceDropdownWrapper: {
    display: 'inline-block',
  },
});
